import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { track } from "../../lib/institutional";

const NAV = [
  { label: "Início", to: "/" },
  { label: "Descubra João Pessoa", to: "/turismo-em-joao-pessoa" },
  { label: "Para Empresas", to: "/para-empresas" },
  { label: "Como Funciona", to: "/como-funciona" },
  { label: "Planos", to: "/#planos" },
  { label: "Sobre", to: "/sobre" },
  { label: "Contato", to: "/contato" },
];

function goPlans(navigate, close) {
  navigate("/#planos");
  close && close();
  setTimeout(() => document.getElementById("planos")?.scrollIntoView({ behavior: "smooth" }), 120);
}

export default function SiteHeader({ transparentTop = false }) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(!transparentTop);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!transparentTop) { setScrolled(true); return; }
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentTop]);

  const solid = scrolled || open;
  const linkBase = "text-sm font-medium transition-colors";
  const linkColor = solid ? "text-slate-600 hover:text-primary" : "text-white/90 hover:text-white";

  const handleNav = (item) => {
    if (item.to === "/#planos") { goPlans(navigate); return; }
    navigate(item.to);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          solid ? "bg-white/90 backdrop-blur-xl shadow-sm py-2.5" : "bg-transparent py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between gap-4">
          <Link to="/" data-testid="site-logo" className="flex items-center shrink-0" onClick={() => track("nav_logo")}>
            <img src="/logo-h.webp" alt="Guia de Jampa AI" width="220" height="115" className={`w-auto object-contain transition-all ${solid ? "h-[50px] md:h-14" : "h-14 md:h-[67px]"}`} />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((it) => (
              <button key={it.label} data-testid={`site-nav-${it.label}`} onClick={() => handleNav(it)} className={`${linkBase} ${linkColor}`}>
                {it.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button data-testid="site-login" onClick={() => navigate("/login")} className={`${linkBase} px-3 py-2 rounded-full ${solid ? "text-slate-700 hover:bg-slate-100" : "text-white hover:bg-white/15"}`}>Entrar</button>
            <button data-testid="site-register" onClick={() => navigate("/cadastro")} className={`${linkBase} px-3 py-2 rounded-full border ${solid ? "border-slate-200 text-slate-700 hover:border-primary hover:text-primary" : "border-white/40 text-white hover:bg-white/15"}`}>Criar Conta</button>
            <button data-testid="site-open-app" onClick={() => { track("open_app", { from: "header" }); navigate("/feed"); }} className="text-sm font-semibold px-4 py-2 rounded-full bg-brand-gradient text-white premium-shadow press flex items-center gap-1.5">
              Acessar o App <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <button data-testid="site-menu-toggle" onClick={() => setOpen((v) => !v)} className={`lg:hidden p-2 rounded-full ${solid ? "text-slate-800 hover:bg-slate-100" : "text-white hover:bg-white/15"}`} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)}>
            <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="site-mobile-drawer"
              className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white p-6 pt-20 flex flex-col gap-1 overflow-y-auto">
              {NAV.map((it) => (
                <button key={it.label} onClick={() => { handleNav(it); setOpen(false); }}
                  className="text-left text-base font-medium text-slate-700 py-3 border-b border-slate-100">
                  {it.label}
                </button>
              ))}
              <div className="mt-4 grid gap-2">
                <button onClick={() => { setOpen(false); navigate("/login"); }} className="w-full py-3 rounded-full border border-slate-200 font-semibold text-slate-700">Entrar</button>
                <button onClick={() => { setOpen(false); navigate("/cadastro"); }} className="w-full py-3 rounded-full border border-primary text-primary font-semibold">Criar Conta</button>
                <button data-testid="site-open-app-mobile" onClick={() => { track("open_app", { from: "drawer" }); setOpen(false); navigate("/feed"); }} className="w-full py-3 rounded-full bg-brand-gradient text-white font-semibold flex items-center justify-center gap-1.5">
                  Acessar o App <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
